import debounce from 'lodash/debounce';
import { SEARCH_QUERY, SEARCH_RESET } from 'events';
import { component } from 'picoapp';
import { clear, hide, show } from 'utils/node';
import getString from 'utils/string';
import { searchProductItem } from 'utils/product-item';
import gtmPush from 'utils/gtm-push';
import Tracking from 'lib/tracking';

export default component((node, ctx) => {
  // #region DOM elements
  const allResultsLink = node.querySelector('[data-all-results-link]');
  const emptyInfo = node.querySelector('[data-empty-info]');
  const input = node.querySelector('[data-input]');
  const searchRecommendationsProducts = node.querySelector('[data-search-recommendations-products]')
  const searchResultsArticles = node.querySelector('[data-search-results-articles]');
  const searchResultsCollections = node.querySelector('[data-search-results-collections]');
  const searchResultsCollectionsList = node.querySelector('[data-search-results-collections-list]');
  const searchResultsPagesList = node.querySelector('[data-search-results-pages-list]');
  const searchResultsArticlesList = node.querySelector('[data-search-results-articles-list]');
  const searchResultsContainer = node.querySelector('[data-search-results-container]');
  const searchResultsPages = node.querySelector('[data-search-results-pages]');
  const searchResultsProducts = node.querySelector('[data-search-results-products]');
  const searchResultsProductsList = node.querySelector('[data-search-results-products-list]');
  const searchSuggestionsContainer = node.querySelector('[data-search-suggestions-container]');
  const searchSuggestions = node.querySelectorAll('[data-search-suggestion]');
  // #endregion

  const reset = () => {
    node.classList.add('empty-state');

    show(searchSuggestionsContainer);

    hide(
      emptyInfo,
      searchRecommendationsProducts,
      searchResultsArticles,
      searchResultsCollections,
      searchResultsPages,
      searchResultsProducts,
    );
  };

  ctx.on(SEARCH_RESET, () => {
    reset();
  });


  const updateAllResultsLink = (query) => {
    if (!allResultsLink) {
      return;
    }

    const url = new URL(window.location.origin);
    url.pathname = window.theme.endpoints.searchPage;
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');

    allResultsLink.href = url.toString();
  };

  const outputProductResults = (products) => {
    if (!products?.length > 0) {
      show(searchRecommendationsProducts);
      hide(searchResultsProducts);

      return;
    }

    hide(searchRecommendationsProducts);

    const html = products.map((product, index) => searchProductItem({
      ...product,
      index,
    })).join('');

    clear(searchResultsProductsList);
    searchResultsProductsList.insertAdjacentHTML('beforeend', html);

    show(searchResultsProducts);
  };

  const searchListItem = (data, trackingAttributes = []) => `
    <li>
      <a href="${data.url}" ${trackingAttributes.join(' ')}>${data.title}</a>
    </li>
  `;

  const outputCollectionResults = (collections) => {
    if (!collections?.length > 0) {
      hide(searchResultsCollections);
      return;
    }

    const html = collections.map((collection) => searchListItem({
      ...collection,
      title: collection.title.split(']').pop().trim(),
    })).join('');

    clear(searchResultsCollectionsList);
    searchResultsCollectionsList.insertAdjacentHTML('beforeend', html);

    show(searchResultsCollections);
  };

  const outputPageResults = (pages) => {
    if (!pages.length > 0) {
      hide(searchResultsPages);
      return;
    }

    const html = pages.map((page) => searchListItem({
      ...page,
    })).join('');

    clear(searchResultsPagesList);
    searchResultsPagesList.insertAdjacentHTML('beforeend', html);

    show(searchResultsPages);
  };

  const outputArticleResults = (articles) => {
    if (!articles.length > 0) {
      hide(searchResultsArticles);
      return;
    }

    const html = articles.map((article) => searchListItem({
      ...article,
    })).join('');

    clear(searchResultsArticlesList);
    searchResultsArticlesList.insertAdjacentHTML('beforeend', html);

    show(searchResultsArticles);
  };

  const outputResults = (query, results) => {
    const { articles, collections, products, pages } = results;

    gtmPush({
      event: 'view_search_results',
      search_parameter: query,
    });

    Tracking.trackViewItemList({
      products: products.map((product) => (
        {
          type: product.type,
          id: product.id,
          title: product.title,
          price: parseFloat(product.price),
        }
      )),
      placement: 'search_flyout',
    });

    const resultsCount = articles?.length + collections?.length + products?.length + pages?.length ?? 0;
    const secondaryResultsCount = resultsCount - products.length;

    if (resultsCount === 0) {
      show(emptyInfo);
      emptyInfo.textContent = getString('search.no_results_header', { query });
    } else {
      hide(emptyInfo);
    }

    if (secondaryResultsCount > 0) {
      hide(searchSuggestionsContainer);
    } else {
      show(searchSuggestionsContainer);
    }

    outputProductResults(results.products);
    outputCollectionResults(results.collections);
    outputPageResults(results.pages);
    outputArticleResults(results.articles);

    document.dispatchEvent(new CustomEvent('app:mount'));

    node.classList.remove('empty-state');
    show(searchResultsContainer);
  };

  const performSearch = async (query) => {
    const url = new URL(window.location.origin);
    url.pathname = `${window.theme.endpoints.predictiveSearch}.json`;
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'article,collection,page,product');
    url.searchParams.set('resources[limit]', 4);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,body');

    const response = await fetch(url);
    const data = await response.json();

    const { resources: { results } } = data;

    return results;
  };

  const handleQuery = async (query) => {
    if (query === '') {
      reset();
    } else {
      const results = await performSearch(query);

      updateAllResultsLink(query);
      outputResults(query, results);
    }
  };

  const initInput = () => {
    ctx.on(SEARCH_QUERY, ({ search: { query } }) => {
      handleQuery(query);
    });

    input.addEventListener('input', debounce(() => {
      const { search: currentState } = ctx.getState();
      ctx.emit(SEARCH_QUERY, { search: { ...currentState, query: input.value } });
    }), 400);
  };

  const initSuggestions = () => {
    searchSuggestions.forEach((suggestion) => {
      suggestion.addEventListener('click', () => {
        gtmPush({
          event: 'search_link_click',
          search_parameter: suggestion.dataset.value,
        });

        input.value = suggestion.dataset.value;
        input.dispatchEvent(new Event('input'));
      });
    });
  };

  initSuggestions();
  initInput();
});
